import { c as _c } from "react/compiler-runtime";
import { Button } from '@systemeio/components';
import { ChevronRightIcon } from '@systemeio/icons';
import { Chart as ChartJS, ChartOptions } from 'chart.js';
import { ContactFilterDateConditionEnum, ContactSearchFieldEnum } from 'modules/contacts/enums/contact-enum';
import { getConditions } from 'modules/contacts/utils/get-conditions';
import { ChartsSkeleton } from 'modules/main/components/chart-box-section/charts-skeleton';
import { MainChartEmptyState } from 'modules/main/components/chart-box-section/main-chart-empty-state';
import { MainChartTrend } from 'modules/main/components/chart-box-section/main-chart-trend';
import { MAIN_CHART_AREA_CLASS_NAME } from 'modules/main/constants/main-chart';
import { useMainLeads } from 'modules/main/hooks/use-main-leads';
import { MainFilterInterface } from 'modules/main/types/main-filter-interface';
import { MainLeadsInterface } from 'modules/main/types/main-interface';
import { useRouter } from 'next/router';
import { MouseEvent, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import { ChartCard } from 'shared/components/chart/chart-card';
import { barChartOptions, barChartPlugins, externalTooltipHandler, withTooltipTitles } from 'shared/components/chart/chart-config';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useTouchDevice } from 'shared/providers/touch-device-provider';
import { toFullDate, toShortDate } from 'shared/utils/date-time-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
interface LeadsChartBoxProps {
  filter: MainFilterInterface;
}

/* Without a day it is the plain contacts list: the link only shows once a column is tapped. */
const getContactsHref = (date?: string) => {
  const pathname = getPageRoute(pageRoute.Contacts);
  if (!date) return pathname;
  const conditions = getConditions({
    filter: {
      [ContactSearchFieldEnum.created_at]: [{
        compareOperation: ContactFilterDateConditionEnum.equals,
        searchValue: `${date}T00:00:00`
      }]
    }
  });
  return `${pathname}?${new URLSearchParams({
    filter: JSON.stringify(conditions)
  })}`;
};
const LeadsChart = ({
  leads
}: {
  leads: MainLeadsInterface;
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const router = useRouter();
  const isTouchDevice = useTouchDevice();

  /*
   * A tap only shows the tooltip, so touch devices reach the drill-down through a link in it. The
   * link is portaled into this node, which the tooltip takes in. It is rendered before the first
   * tap, so the tooltip is measured with it. The design keeps it 2px under the value, while the
   * tooltip spaces its rows 8px apart.
   */
  const [contactsLink] = useState(() => {
    if (typeof document === 'undefined') return null;
    const element = document.createElement('div');
    element.className = 'pointer-events-auto -mt-1.5 flex justify-center';
    return element;
  });
  const [activeIndex, setActiveIndex] = useState<number>();

  /*
   * The hovered column is resolved by index rather than by intersecting the bar itself, so the
   * drill-down and the pointer cursor cover the same area the tooltip already reacts to.
   */
  const getHoveredIndex = (event: MouseEvent) => chartRef.current?.getElementsAtEventForMode(event as unknown as Event, 'index', {
    intersect: false
  }, true).at(0)?.index;
  const data = useMemo(() => ({
    labels: leads.values.map(({
      date
    }) => toShortDate(date, intlLocale)),
    datasets: [{
      data: leads.values.map(({
        value
      }) => value)
    }]
  }), [intlLocale, leads]);
  const options = useMemo((): ChartOptions<'bar'> => {
    const withTitles = withTooltipTitles<'bar'>(barChartOptions, leads.values.map(({
      date: date_0
    }) => toFullDate(date_0, intlLocale)));
    if (!isTouchDevice || !contactsLink) return withTitles;
    return {
      ...withTitles,
      onHover: (_event, elements) => setActiveIndex(elements.at(0)?.index),
      plugins: {
        ...withTitles.plugins,
        tooltip: {
          ...withTitles.plugins?.tooltip,
          external: context => {
            /* The fading tooltip keeps the link, which must stop taking taps meant for the chart. */
            contactsLink.style.visibility = context.tooltip.opacity ? '' : 'hidden';
            externalTooltipHandler(context, contactsLink);
          }
        }
      }
    };
  }, [contactsLink, intlLocale, isTouchDevice, leads]);
  const onClick = (event_0: MouseEvent<HTMLCanvasElement>) => {
    if (isTouchDevice) return;
    const index = getHoveredIndex(event_0);
    if (index === undefined) return;
    const date_1 = leads.values[index]?.date;
    if (user && date_1) router.push(getContactsHref(date_1));
  };
  const onPointerMove = (event_1: MouseEvent) => {
    if (chartRef.current) {
      chartRef.current.canvas.style.cursor = getHoveredIndex(event_1) === undefined ? 'default' : 'pointer';
    }
  };
  return <ChartCard title={t('dashboard.main.new_leads')} headerAction={<MainChartTrend countDays={leads.countDays} diffPercents={leads.diffPercents} />} value={new Intl.NumberFormat(intlLocale).format(leads.total)} valueLabel={t('dashboard.funnel.statistics.total')}>
      <div className={MAIN_CHART_AREA_CLASS_NAME}>
        <Bar ref={chartRef} onClick={onClick} onPointerMove={onPointerMove} data={data} options={options} plugins={barChartPlugins} />
      </div>
      {isTouchDevice && contactsLink && createPortal(<Button variant={'link'} render={<LinkWithoutPrefetch href={getContactsHref(activeIndex === undefined ? undefined : leads.values[activeIndex]?.date)} />}>
            {t('dashboard.main.chart.added_contacts_link')}
            <ChevronRightIcon />
          </Button>, contactsLink)}
    </ChartCard>;
};
export const LeadsChartBox = t0 => {
  const $ = _c(30);
  const {
    filter
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    intlLocale
  } = useUser();
  const {
    data: leads,
    error
  } = useMainLeads(filter);
  if (error) {
    let t1;
    if ($[0] !== t) {
      t1 = t("dashboard.main.new_leads");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t) {
      t2 = t("core.error.internal_error_message");
      $[2] = t;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <MainChartEmptyState title={t2} />;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t1 || $[7] !== t3) {
      t4 = <ChartCard title={t1}>{t3}</ChartCard>;
      $[6] = t1;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (!leads) {
    let t1;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ChartsSkeleton />;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    return t1;
  }
  if (!leads.total) {
    let t1;
    if ($[10] !== t) {
      t1 = t("dashboard.main.new_leads");
      $[10] = t;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    let t2;
    if ($[12] !== intlLocale) {
      t2 = new Intl.NumberFormat(intlLocale).format(0);
      $[12] = intlLocale;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== t) {
      t3 = t("dashboard.funnel.statistics.total");
      $[14] = t;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== t) {
      t4 = t("dashboard.affiliate_dashboard.leads.empty_filter.title");
      $[16] = t;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== t) {
      t5 = t("dashboard.main.chart.empty_description");
      $[18] = t;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== t4 || $[21] !== t5) {
      t6 = <MainChartEmptyState title={t4} description={t5} />;
      $[20] = t4;
      $[21] = t5;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    let t7;
    if ($[23] !== t1 || $[24] !== t2 || $[25] !== t3 || $[26] !== t6) {
      t7 = <ChartCard title={t1} value={t2} valueLabel={t3}>{t6}</ChartCard>;
      $[23] = t1;
      $[24] = t2;
      $[25] = t3;
      $[26] = t6;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    return t7;
  }
  let t1;
  if ($[28] !== leads) {
    t1 = <LeadsChart leads={leads} />;
    $[28] = leads;
    $[29] = t1;
  } else {
    t1 = $[29];
  }
  return t1;
};